import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Grid,
  IconButton,
  Icon,
  Select,
  MenuItem,
  TextField,
  Grow
} from '@material-ui/core';
import { updateUserApi, getAllPermissionConfigsApi, deletePermissionConfigByIdApi, getUsersAPI, getStoreUsersExportApi } from 'api';
import MUIDataTable from 'mui-datatables';
import { BackdropCircular } from 'components';
import EditPermissionConfigModal from './EditPermissionConfigModal';


const options = {
  filterArrayFullMatch: false,
  filterType: 'textField',
  responsive: 'standard',
  selectableRows: 'none', // set checkbox for each row
  search: true, // set search option
  filter: false, // set data filter option
  download: true, // set download option
  print: false, // set print option
  pagination: false, //set pagination option
  viewColumns: true, // set column option
  elevation: 0,
};

const FeatureFlipper = () => {
  const dispatch = useDispatch();
  const [editableConfigData, setEditableConfigData] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [email, setEmail] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { loadPermissionConfigs, permissionConfigs, generalPermissions, users, usersTotalCount, downloadingUsersReport } = useSelector((state) => state.admin);
  const {profile} = useSelector((state) => state.user);
  const permissions = profile?.config?.permissions || {};

  useEffect(() => {
    getAllPermissionConfigsApi({dispatch});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams({
      email,
      page: String(page),
      limit: String(limit),
    }).toString();

    getUsersAPI({dispatch, params});
  }, [
    email,
    page,
    limit
  ]);

  const handleChangeUserConfig = (configId, user) => {
    const config = permissionConfigs.find((c) => c._id === configId);
    updateUserApi({ dispatch, userProfile: { config }, userId: user._id });
  };

  const onSearch = (searchValue) => {
    setEmail(searchValue);
    setPage(1);
    setLimit(20);
  };

  const configColumns = [
    {
      name: '_id',
      label: 'Id',
    },
    {
      name: 'name',
      label: 'Name',
    },
    {
      name: 'createdAt',
      label: 'Date',
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <Grid>
              {permissions?.adminFeatureFlipperEditConfig &&
              <IconButton
                title="Edit config"
                onClick={() => setEditableConfigData(permissionConfigs[dataIndex])}
              >
                <Icon>edit</Icon>
              </IconButton>}
              {permissionConfigs[dataIndex].type !== 'general' && permissions?.adminFeatureFlipperRemoveConfig &&
              <IconButton
                title="Delete config"
                onClick={() => deletePermissionConfigByIdApi({ dispatch, id: permissionConfigs[dataIndex]._id})}
              >
                <Icon>delete</Icon>
              </IconButton>}
            </Grid>
          );
        }
      }
    }
  ];

  const usersColumns = [
    {
      name: 'user',
      label: 'User Name',
      options: {
        filter: true,
        setCellHeaderProps: () => ({ style: { width: '150px' } }),
        customBodyRenderLite: (dataIndex) => {
          const user = users[dataIndex];

          return (
            <div className="flex items-center">
              {user.firstName} {user.lastName}
            </div>
          );
        },
      }
    },
    {
      name: 'email',
      label: 'User Email',
      options: {
        setCellHeaderProps: () => ({ style: { width: '250px' } }),
      }
    },
    {
      name: 'store',
      label: 'Store Code',
      options: {
        setCellHeaderProps: () => ({ style: { width: '150px' } }),
        customBodyRenderLite: (dataIndex) => {
          const {stores} = users[dataIndex];

          return (
            <div className="items-center">
              {stores[0]?.storeCode}
            </div>
          );
        },
      }
    },
    {
      name: 'storeUrl',
      label: 'User Store',
      options: {
        setCellHeaderProps: () => ({ style: { width: '350px' } }),
        customBodyRenderLite: (dataIndex) => {
          const {stores} = users[dataIndex];

          return (
            <div className="items-center">
              {stores[0]?.storeUrl}
            </div>
          );
        },
      }
    },
    {
      name: 'edit',
      label: 'Config',
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <Select
              name="config"
              disabled={!permissions?.adminFeatureFlipperChangeUserConfig}
              value={users[dataIndex].config?._id}
              label="Config"
              onChange={(e) => handleChangeUserConfig(e.target.value, users[dataIndex])}
            >
              {permissionConfigs.map((config) => <MenuItem key={config?._id} value={config?._id}>{config?.name}</MenuItem>)}
            </Select>
          );
        }
      }
    }
  ];

  return (
    <div>
      {!loadPermissionConfigs &&
      <>
        {permissions?.adminFeatureFlipperAddConfig &&
        <div className="pt-8 flex justify-start">
          <Button
            variant="contained"
            color="primary"
            onClick={() => setEditableConfigData({ permissions: generalPermissions })}
          >
            Create New Config
          </Button>
        </div>}
        {editableConfigData &&
          <EditPermissionConfigModal
            generalPermissions={generalPermissions}
            setOpen={setEditableConfigData}
            configData={editableConfigData}
          />
        }
        <MUIDataTable
          className="mt-10"
          title={'Configs'}
          data={permissionConfigs}
          columns={configColumns}
          options={options}
        />

        <MUIDataTable
          className="mt-10"
          title={'Users'}
          data={users}
          columns={usersColumns}
          options={{
            filterType: 'textField',
            selectableRows: 'none',
            rowsPerPageOptions: [10, 20, 50, 100],
            serverSide: true,
            search: true,
            customSearchRender: (searchText, handleSearch, hideSearch) => {
              return (
                <Grow appear in timeout={300}>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={searchValue}
                    onChange={({ target: { value } }) => {
                      setSearchValue(value);
                    }}
                    onKeyPress={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        onSearch(searchValue);
                      }
                    }}
                    placeholder="Enter user email"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => {
                            setSearchValue('');
                            onSearch('');
                            hideSearch();
                          }}
                        >
                          <Icon fontSize="small">
                            clear
                          </Icon>
                        </IconButton>
                      ),
                    }}
                  />
                </Grow>
              );
            },
            filter: false,
            sort: false,
            download: true,
            print: true,
            viewColumns: true,
            rowsPerPage: limit,
            page: page - 1,
            count: usersTotalCount,
            onDownload: () => {
              getStoreUsersExportApi({ dispatch });

              return false;
            },
            onChangePage: (page) => setPage(page + 1),
            onChangeRowsPerPage: (newPageSize) => {
              setLimit(newPageSize);
              setPage(1);
            },
            setTableProps: () => ({
              headRow: {
                fontWeight: 'bold',
              },
            }),
          }}
        />
      </>
      }
      <BackdropCircular loading={loadPermissionConfigs || downloadingUsersReport} />
    </div>
  );
};


export default FeatureFlipper;
